import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import en from '@angular/common/locales/en';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, registerLocaleData } from '@angular/common';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { LIB_CONFIG, MenuPermissionService } from 'shared-lib';

import { routes } from './app.routes';
import { environment } from '../../src/environments/environment';
import { authInterceptor } from './core/auth/auth.interceptor';
import { authErrorsInterceptor } from './core/auth/auth-error.interceptor';
import { UserService } from './core/user/user.service';
import { AuthGuard } from './core/auth/auth.guard';
import { AppTranslationService } from './core/translation/translation.service';
import { DEFAULT_LANGUAGE } from './custom.config';
import { LocalStorageService } from '@selfCareCore/storage/local-storage.service';
import { LOCAL_STORAGE_KEYS } from '@selfCareCore/storage/storage.constant';
import { cacheInterceptor } from '@selfCareCore/interceptors/cache.interceptor';
import { languageInterceptor } from '@selfCareCore/interceptors/language.interceptor';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

function initializeApp(): () => void {
  const userService = inject(UserService);
  return () => {
    userService.initialize();
  };
}

export const DATE_TIME_FORMAT = {
  DATE: 'dd/MM/yyyy',
  DATE_TIME: 'dd/MM/yyyy hh:mm:ss a',
};

registerLocaleData(en);

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      // Keeps the browser history index in sync with the router when a CanDeactivate guard
      // cancels a browser back/forward navigation, preventing the confirmation dialog from
      // being triggered a second time by a mismatched history entry.
      withRouterConfig({ canceledNavigationResolution: 'computed' })
    ),
    provideHttpClient(
      withInterceptors([languageInterceptor, authInterceptor, authErrorsInterceptor, cacheInterceptor])
    ),
    provideAnimations(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    importProvidersFrom(NzModalModule),
    provideAnimationsAsync(),
    DatePipe,
    {
      provide: LIB_CONFIG,
      useValue: {
        API_BASE_URL: `${environment.baseUrl}/api`,
      },
    },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: DATE_TIME_FORMAT.DATE } },
    { provide: MenuPermissionService, useExisting: null },
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (): (() => Promise<any>) => {
        const translateService = inject(TranslateService);
        const localStorage = inject(LocalStorageService);

        return () => {
          const savedLang = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_LANGUAGE) || DEFAULT_LANGUAGE;
          // Set the language WITHOUT reloading
          return translateService.use(savedLang.toLowerCase()).toPromise();
        };
      },
      multi: true,
    },
    AuthGuard,
    provideCharts(withDefaultRegisterables()),
  ],
};
